import React, { useCallback, useState } from "react";
import { useFirebase } from "../contexts/FirebaseContext";

const BOARDS = [
  { id: "dino", label: "Dino Run" },
  { id: "maze", label: "Maze" },
  { id: "solitaire", label: "Solitaire" },
  { id: "opsTickles", label: "Ops-tickles" },
];

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return value.toLocaleString();
  } catch {
    return "—";
  }
};

const formatTime = (timeSec) => {
  if (timeSec == null || Number.isNaN(Number(timeSec))) return "—";
  const total = Math.max(0, Number(timeSec));
  const mins = Math.floor(total / 60);
  const secs = (total % 60).toFixed(1);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const emptyDraftForBoard = (boardId, row) => {
  if (boardId === "solitaire") {
    return {
      displayName: row?.displayName || "",
      timeSec: String(row?.timeSec ?? ""),
      moves: String(row?.moves ?? ""),
    };
  }
  if (boardId === "maze") {
    return {
      displayName: row?.displayName || "",
      timeSec: String(row?.timeSec ?? ""),
      moves: String(row?.moves ?? ""),
      level: String(row?.level ?? ""),
    };
  }
  if (boardId === "opsTickles") {
    return {
      displayName: row?.displayName || "",
      points: String(row?.points ?? row?.score ?? ""),
    };
  }
  return {
    displayName: row?.displayName || "",
    score: String(row?.score ?? ""),
  };
};

const AdminLeaderboards = () => {
  const { adminGetAllScores, adminUpdateScore, adminDeleteScore } =
    useFirebase();
  const [open, setOpen] = useState(false);
  const [activeBoard, setActiveBoard] = useState("dino");
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [draft, setDraft] = useState(null);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadScores = useCallback(
    async (boardId) => {
      setLoading(true);
      setError("");
      try {
        const rows = await adminGetAllScores(boardId);
        setScores(rows);
      } catch (err) {
        console.error(err);
        setError("Could not load leaderboard. Check Firebase permissions.");
        setScores([]);
      } finally {
        setLoading(false);
      }
    },
    [adminGetAllScores]
  );

  const handleOpen = async () => {
    setOpen(true);
    setMessage("");
    setError("");
    await loadScores(activeBoard);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    setError("");
    setMessage("");
    setEditingRow(null);
    setDraft(null);
    setModalError("");
  };

  const handleBoardChange = async (boardId) => {
    if (boardId === activeBoard) return;
    setActiveBoard(boardId);
    setMessage("");
    setEditingRow(null);
    setDraft(null);
    await loadScores(boardId);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setDraft(emptyDraftForBoard(activeBoard, row));
    setModalError("");
    setMessage("");
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditingRow(null);
    setDraft(null);
    setModalError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!editingRow || !draft) return;

    const displayName = String(draft.displayName || "").trim();
    if (!displayName) {
      setModalError("Display name is required.");
      return;
    }

    const fields = { displayName };
    if (activeBoard === "dino") {
      if (draft.score.trim() === "" || Number.isNaN(Number(draft.score))) {
        setModalError("Enter a valid score.");
        return;
      }
      fields.score = Number(draft.score);
    } else if (activeBoard === "opsTickles") {
      if (draft.points.trim() === "" || Number.isNaN(Number(draft.points))) {
        setModalError("Enter a valid points value.");
        return;
      }
      fields.points = Number(draft.points);
    } else if (activeBoard === "maze" || activeBoard === "solitaire") {
      if (draft.timeSec.trim() === "" || Number.isNaN(Number(draft.timeSec))) {
        setModalError("Enter a valid time in seconds.");
        return;
      }
      if (draft.moves.trim() === "" || Number.isNaN(Number(draft.moves))) {
        setModalError("Enter a valid move count.");
        return;
      }
      fields.timeSec = Number(draft.timeSec);
      fields.moves = Number(draft.moves);
      if (activeBoard === "maze") {
        if (draft.level.trim() === "" || Number.isNaN(Number(draft.level))) {
          setModalError("Enter a valid level.");
          return;
        }
        fields.level = Number(draft.level);
      }
    }

    setSaving(true);
    setModalError("");
    try {
      const updated = await adminUpdateScore(
        activeBoard,
        editingRow.id,
        fields
      );
      setScores((current) => {
        const next = current.map((row) =>
          row.id === editingRow.id
            ? {
                ...row,
                displayName: updated.displayName ?? row.displayName,
                score: updated.score ?? row.score,
                points: updated.points ?? row.points,
                timeSec: updated.timeSec ?? row.timeSec,
                moves: updated.moves ?? row.moves,
                level: updated.level ?? row.level,
              }
            : row
        );
        if (activeBoard === "maze" || activeBoard === "solitaire") {
          return [...next].sort(
            (a, b) =>
              (a.timeSec ?? Number.POSITIVE_INFINITY) -
              (b.timeSec ?? Number.POSITIVE_INFINITY)
          );
        }
        if (activeBoard === "opsTickles") {
          return [...next].sort(
            (a, b) =>
              (b.points ?? b.score ?? 0) - (a.points ?? a.score ?? 0)
          );
        }
        return [...next].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
      });
      setMessage(`Updated score for ${displayName}.`);
      setEditingRow(null);
      setDraft(null);
    } catch (err) {
      console.error(err);
      setModalError("Could not update score. Check Firebase permissions.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const label = row.displayName || row.userId || "this score";
    const confirmed = window.confirm(
      `Delete score for "${label}"? This cannot be undone.`
    );
    if (!confirmed) return;

    const previous = scores;
    setScores((current) => current.filter((item) => item.id !== row.id));
    setDeletingId(row.id);
    setError("");
    setMessage("");

    try {
      await adminDeleteScore(activeBoard, row.id);
      setMessage(`Deleted score for ${label}.`);
    } catch (err) {
      console.error(err);
      setScores(previous);
      setError("Could not delete score. Try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const primaryValue = (row) => {
    if (activeBoard === "maze" || activeBoard === "solitaire") return formatTime(row.timeSec);
    if (activeBoard === "opsTickles") return row.points ?? row.score ?? "—";
    return row.score ?? "—";
  };

  return (
    <>
      <button
        type="button"
        className="admin-button admin-button--ghost"
        onClick={handleOpen}
      >
        Leaderboards
      </button>

      {open && (
        <div className="admin-online-overlay" onClick={handleClose}>
          <div
            className="admin-online-panel admin-leaderboards-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-leaderboards-title"
          >
            <div className="admin-online-header">
              <div>
                <h2 id="admin-leaderboards-title">Leaderboards</h2>
                <p className="admin-online-sub">
                  Production high scores — view, edit, or delete entries.
                </p>
              </div>
              <div className="admin-online-header-actions">
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={() => loadScores(activeBoard)}
                  disabled={loading}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="admin-leaderboards-tabs" role="tablist">
              {BOARDS.map((board) => (
                <button
                  key={board.id}
                  type="button"
                  role="tab"
                  aria-selected={activeBoard === board.id}
                  className={`admin-leaderboards-tab ${
                    activeBoard === board.id ? "is-active" : ""
                  }`}
                  onClick={() => handleBoardChange(board.id)}
                >
                  {board.label}
                </button>
              ))}
            </div>

            {error && <p className="admin-error">{error}</p>}
            {message && <p className="admin-success">{message}</p>}

            <div className="admin-table-header">
              <h3 className="admin-leaderboards-board-title">
                {BOARDS.find((board) => board.id === activeBoard)?.label}
              </h3>
              {loading ? (
                <span className="admin-muted">Loading…</span>
              ) : (
                <span className="admin-muted">{scores.length} entries</span>
              )}
            </div>

            {!loading && scores.length === 0 && !error && (
              <p className="admin-muted">No scores on this board yet.</p>
            )}

            {scores.length > 0 && (
              <div className="admin-table-wrap">
                <table className="admin-table admin-table--leaderboards">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>
                        {activeBoard === "maze" || activeBoard === "solitaire"
                          ? "Time"
                          : activeBoard === "opsTickles"
                            ? "Points"
                            : "Score"}
                      </th>
                      {(activeBoard === "maze" || activeBoard === "solitaire") && (
                        <th>Moves</th>
                      )}
                      {activeBoard === "maze" && <th>Level</th>}
                      <th>When</th>
                      <th>User</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((row, index) => (
                      <tr key={row.id}>
                        <td>{index + 1}</td>
                        <td>{row.displayName || "—"}</td>
                        <td className="admin-points">{primaryValue(row)}</td>
                        {(activeBoard === "maze" || activeBoard === "solitaire") && (
                          <td>{row.moves ?? "—"}</td>
                        )}
                        {activeBoard === "maze" && <td>{row.level ?? "—"}</td>}
                        <td>{formatDate(row.timestamp)}</td>
                        <td className="admin-userid">{row.userId || "—"}</td>
                        <td>
                          <div className="admin-leaderboards-actions">
                            <button
                              type="button"
                              className="admin-points-edit"
                              onClick={() => openEditModal(row)}
                              aria-label={`Edit score for ${
                                row.displayName || row.userId || "entry"
                              }`}
                              title="Edit score"
                            >
                              <PencilIcon />
                            </button>
                            <button
                              type="button"
                              className="admin-claim-delete"
                              disabled={deletingId === row.id}
                              onClick={() => handleDelete(row)}
                            >
                              {deletingId === row.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {editingRow && draft && (
        <div className="admin-modal-overlay" onClick={closeEditModal}>
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-score-modal-title"
          >
            <h3 id="admin-score-modal-title">Edit score</h3>
            <p className="admin-modal-sub">
              {editingRow.displayName || "Unnamed"}
              <span className="admin-modal-userid">
                {editingRow.userId || editingRow.id}
              </span>
            </p>

            <form className="admin-modal-form" onSubmit={handleSave}>
              <label className="admin-label" htmlFor="admin-score-name">
                Display name
              </label>
              <input
                id="admin-score-name"
                className="admin-input"
                type="text"
                value={draft.displayName}
                onChange={(e) => {
                  setDraft((current) => ({
                    ...current,
                    displayName: e.target.value,
                  }));
                  setModalError("");
                }}
                disabled={saving}
                autoFocus
              />

              {activeBoard === "dino" && (
                <>
                  <label className="admin-label" htmlFor="admin-score-value">
                    Score
                  </label>
                  <input
                    id="admin-score-value"
                    className="admin-input"
                    type="number"
                    min={0}
                    step={1}
                    value={draft.score}
                    onChange={(e) => {
                      setDraft((current) => ({
                        ...current,
                        score: e.target.value,
                      }));
                      setModalError("");
                    }}
                    disabled={saving}
                  />
                </>
              )}

              {activeBoard === "opsTickles" && (
                <>
                  <label className="admin-label" htmlFor="admin-score-points">
                    Points
                  </label>
                  <input
                    id="admin-score-points"
                    className="admin-input"
                    type="number"
                    min={0}
                    step={1}
                    value={draft.points}
                    onChange={(e) => {
                      setDraft((current) => ({
                        ...current,
                        points: e.target.value,
                      }));
                      setModalError("");
                    }}
                    disabled={saving}
                  />
                </>
              )}

              {(activeBoard === "maze" || activeBoard === "solitaire") && (
                <>
                  <label className="admin-label" htmlFor="admin-score-time">
                    Time (seconds)
                  </label>
                  <input
                    id="admin-score-time"
                    className="admin-input"
                    type="number"
                    min={0}
                    step={0.1}
                    value={draft.timeSec}
                    onChange={(e) => {
                      setDraft((current) => ({
                        ...current,
                        timeSec: e.target.value,
                      }));
                      setModalError("");
                    }}
                    disabled={saving}
                  />
                  <label className="admin-label" htmlFor="admin-score-moves">
                    Moves
                  </label>
                  <input
                    id="admin-score-moves"
                    className="admin-input"
                    type="number"
                    min={0}
                    step={1}
                    value={draft.moves}
                    onChange={(e) => {
                      setDraft((current) => ({
                        ...current,
                        moves: e.target.value,
                      }));
                      setModalError("");
                    }}
                    disabled={saving}
                  />
                  {activeBoard === "maze" && (
                    <>
                      <label className="admin-label" htmlFor="admin-score-level">
                        Level
                      </label>
                      <input
                        id="admin-score-level"
                        className="admin-input"
                        type="number"
                        min={1}
                        step={1}
                        value={draft.level}
                        onChange={(e) => {
                          setDraft((current) => ({
                            ...current,
                            level: e.target.value,
                          }));
                          setModalError("");
                        }}
                        disabled={saving}
                      />
                    </>
                  )}
                </>
              )}

              {modalError && <p className="admin-error">{modalError}</p>}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-button"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save score"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLeaderboards;
